import React, { Fragment } from 'react';
import { Breakpoint } from 'react-socks';
import { common } from '@kurtosys/ksys-app-template';
import { action, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import { InjectedStyledComponent, ResponsiveComponentBase } from '../../../shared/index.js';
import Carousel from '../../base/Carousel/Carousel.js';
import Award from '../Award/Award.js';

import { IAward } from './models/IAward.js';
import { IAwardsConfiguration, IAwardsProps, IAwardsState } from './models/index.js';
import AwardWrapper from './styledComponents/AwardWrapper.js';
import Wrapper from './styledComponents/Wrapper.js';
import { AWARDS_CATEGORY_KEY, AWARDS_COMPONENT_KEY } from './Awards.meta.js';

const sortByFunction = common.commonUtils.sortBy;

export class Awards extends ResponsiveComponentBase<IAwardsProps, IAwardsState, IAwardsConfiguration> {
	categoryKey = AWARDS_CATEGORY_KEY;
	componentKey = AWARDS_COMPONENT_KEY;

	awards: IAward[] = [];

	constructor(props: IAwardsProps) {
		super(props);

		makeObservable(this, {
			awards: observable,
			setHidden: action,
			setLoaded: action,
		});

		const { awards = [] } = props;

		this.awards = [
			...awards.map((award, index) => {
				return {
					...award,
					imageProps: {
						...award.imageProps,
						onError: () => {
							this.setHidden(award, index);
						},
						onLoad: () => {
							this.setLoaded(index);
						},
					},
				};
			}),
		];
	}

	setHidden = (award: IAward, index: number) => {
		this.awards[index] = {
			...award,
			hidden: true,
			loaded: true,
		};
		this.awards = [...this.awards];
	};

	setLoaded = (index: number) => {
		this.awards[index].loaded = true;
		this.awards = [...this.awards];
	};

	imagesLoaded(hideAwardsWithNoImages?: boolean) {
		if (hideAwardsWithNoImages) {
			return this.awards.every((award) => award.loaded);
		}
		return true;
	}

	getSortedAwards(awards: IAward[], sortBy?: (value: IAward) => string) {
		if (common.commonUtils.isNullOrUndefined(sortBy)) {
			return awards;
		}
		return sortByFunction(awards, sortBy, 'ASC');
	}

	getAwardsCarousel(props: IAwardsProps) {
		const { carouselProps } = props;
		return <Carousel {...(carouselProps || {})}>{this.getAwards(props)}</Carousel>;
	}

	getValidAwards(awards: IAward[]) {
		return awards.filter((award) => !award.hidden);
	}

	getAwards(props: IAwardsProps) {
		const { hideAwardsWithNoImages, sortBy } = props;
		let awards: IAward[] = this.awards;
		if (hideAwardsWithNoImages) {
			awards = this.getValidAwards(awards);
		}
		awards = this.getSortedAwards(awards, sortBy);
		return awards.map((award: IAward, index: number) => {
			const { order, ...awardProps } = award;
			if (props.textProps) {
				awardProps.textProps = common.commonUtils.deepMergeObjects(props.textProps, awardProps.textProps);
			}
			return (
				<AwardWrapper key={`award_${index}`}>
					<Award {...awardProps} />
				</AwardWrapper>
			);
		});
	}

	render() {
		const breakpoints = this.breakpoints;

		return (
			<Fragment>
				{breakpoints.map((breakpoint, breakpointIndex) => {
					const { props } = breakpoint;
					if (props) {
						const breakpointProps = this.getBreakpointComponentProps(breakpoint, breakpointIndex);
						const { className, useCarousel, hideAwardsWithNoImages } = props;
						return (
							<Breakpoint {...breakpointProps}>
								<Wrapper
									className={className}
									style={{
										visibility: this.imagesLoaded(hideAwardsWithNoImages) ? 'visible' : 'hidden',
									}}
								>
									{useCarousel ? this.getAwardsCarousel(props) : this.getAwards(props)}
								</Wrapper>
							</Breakpoint>
						);
					}
				})}
			</Fragment>
		);
	}
}

export default InjectedStyledComponent(observer(Awards), AWARDS_CATEGORY_KEY, AWARDS_COMPONENT_KEY);
